import type { ReactNode } from 'react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/modules/ui/components/card/Card';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';

import { NoteForm, type NoteFormData, type NoteTypeOption } from './NoteForm';

type Note = {
  id: string;
  createdAt: string;
  updatedAt?: string | null;
  type: string;
  content: string;
};

interface NotesListProps<TNote extends Note> {
  notes?: TNote[];
  isLoading?: boolean;
  onAddNote: (data: NoteFormData) => void;
  isCreating?: boolean;
  title?: string;
  description?: string;
  renderNoteCard: (note: TNote) => ReactNode;
  typeOptions: readonly NoteTypeOption[];
  defaultType?: string;
}

export const NotesList = <TNote extends Note>({
  notes,
  isLoading = false,
  onAddNote,
  isCreating = false,
  title = 'Notes',
  description = 'Keep track of important information',
  renderNoteCard,
  typeOptions,
  defaultType,
}: NotesListProps<TNote>) => {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasNotes = notes && notes.length > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {hasNotes && (
          <div className="mb-6 space-y-4">
            {notes.map((note) => (
              <div key={note.id}>{renderNoteCard(note)}</div>
            ))}
          </div>
        )}

        <NoteForm
          key={notes?.length || 0}
          onSubmit={onAddNote}
          isLoading={isCreating}
          typeOptions={typeOptions}
          defaultType={defaultType}
        />
      </CardContent>
    </Card>
  );
};
